/*
 * csvFormResolve.ts — turn the words a CSV row uses for a form into the
 * registry's formId (2026-10-12, Nexus Dex CSV template).
 *
 * Two columns feed this. Poké Genie's own `Form` column carries a label
 * ("Alola", "Galarian", "Origin", "Normal") that the import used to store as
 * display text and nothing else, so an imported Alolan Marowak drew the Kanto
 * sprite and never ticked the Regional dex. The Nexus Dex template adds a
 * `Costume` column, which Poké Genie has no notion of, for the people who
 * fill the file in by hand.
 *
 * Every lookup is scoped to ONE species (the row's Pokemon Number) and only
 * ever returns an id that species actually has in the generated registry, so
 * a typo writes nothing rather than a junk form. Matching ignores case,
 * accents and punctuation: "Halloween 2026", "halloween-2026" and
 * "pikachu-halloween-2026" are the same costume.
 */
import { FORM_GROUPS_BY_SPECIES_ID } from "./generated/pokemonForms.generated";
import type { FormVariant } from "./types/forms";

/** Letters and digits only, lower-case, accents stripped. */
export function normaliseFormWord(raw: string): string {
  return raw
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
}

/**
 * The regional labels Poké Genie writes, and the slug segment each one is in
 * the registry (`meowth-galar`, `tauros-paldea-combat-breed`). Both the noun
 * and the adjective, since exports and hand-typed files use either.
 */
const REGION_WORDS: ReadonlyArray<[word: string, segment: string]> = [
  ["alola", "alola"],
  ["alolan", "alola"],
  ["galar", "galar"],
  ["galarian", "galar"],
  ["hisui", "hisui"],
  ["hisuian", "hisui"],
  ["paldea", "paldea"],
  ["paldean", "paldea"],
];

function formsOf(dexNumber: number): readonly FormVariant[] {
  return FORM_GROUPS_BY_SPECIES_ID.get(dexNumber)?.forms ?? [];
}

/**
 * Match `value` against one list of forms: the full formId, the formId with
 * the species prefix dropped ("halloween-2026" for Pikachu), or the display
 * name. Null when nothing matches.
 */
function matchForm(
  forms: readonly FormVariant[],
  speciesSlug: string | undefined,
  value: string,
): string | null {
  const want = normaliseFormWord(value);
  if (!want) return null;
  for (const f of forms) {
    if (normaliseFormWord(f.formId) === want) return f.formId;
    if (normaliseFormWord(f.displayName) === want) return f.formId;
    if (speciesSlug) {
      const prefix = `${speciesSlug}-`;
      if (f.formId.startsWith(prefix) && normaliseFormWord(f.formId.slice(prefix.length)) === want) {
        return f.formId;
      }
    }
  }
  return null;
}

/**
 * The formId behind a `Form` cell, for the species' non-costume forms.
 * Accepts the registry id, the display name, or a regional word; "Normal",
 * an empty cell, and anything the species does not have resolve to null
 * (the base form).
 */
export function resolveCsvFormId(dexNumber: number, value: string | null | undefined): string | null {
  if (!value) return null;
  const group = FORM_GROUPS_BY_SPECIES_ID.get(dexNumber);
  if (!group) return null;
  const forms = group.forms.filter((f) => f.kind !== "costume");
  const direct = matchForm(forms, group.speciesName, value);
  if (direct) return direct;
  const want = normaliseFormWord(value);
  for (const [word, segment] of REGION_WORDS) {
    if (want !== word) continue;
    const re = new RegExp(`(^|-)${segment}(-|$)`);
    const hit = forms.find((f) => re.test(f.formId));
    if (hit) return hit.formId;
  }
  return null;
}

/**
 * The formId behind a `Costume` cell, for the species' costumes only. Null
 * when the cell is empty, and ALSO null when it names something the species
 * never wore, so the caller can count the miss and tell the trainer.
 */
export function resolveCsvCostumeId(dexNumber: number, value: string | null | undefined): string | null {
  if (!value) return null;
  const group = FORM_GROUPS_BY_SPECIES_ID.get(dexNumber);
  if (!group) return null;
  const costumes = group.forms.filter((f) => f.kind === "costume");
  return matchForm(costumes, group.speciesName, value);
}

/** The costumes a species has, for the template's glossary. */
export function costumesForSpecies(dexNumber: number): readonly FormVariant[] {
  return formsOf(dexNumber).filter((f) => f.kind === "costume");
}
